import React, { useState } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  Link,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import Login from './components/Login';
import ResearchReportsListPage from './components/ResearchReportsListPage';
import ResearchReportDetailPage from './components/ResearchReportDetailPage';
import ContentQueue from './components/ContentQueue';
import ResearchBriefs from './components/ResearchBriefs';
import PublishingQueue from './components/PublishingQueue';
import PublishedPlaceholder from './components/PublishedPlaceholder';
import ResearchBriefWorkspace from './components/ResearchBriefWorkspace';
import PublishPreflight from './components/PublishPreflight';
import AccountPage from './components/AccountPlaceholder';
import MarketingLayout from './marketing/MarketingLayout';
import HomePage from './marketing/HomePage';
import BlogIndex from './marketing/BlogIndex';
import BlogPost from './marketing/BlogPost';
import DocsIndex from './marketing/DocsIndex';
import DocsPage from './marketing/DocsPage';
import HelpDocsIndex from './components/HelpDocsIndex';
import HelpDocPage from './components/HelpDocPage';
import './styles/App.css';
import './components/Login.css';

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="auth-loading-screen">Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <AppLayout />;
}

function AppLayout() {
  const [researchOpen, setResearchOpen] = useState(false);
  const [writeOpen, setWriteOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  return (
    <div className="app">
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">
            <h1 className="nav-brand-heading">
              <Link to="/research/reports" className="nav-brand-link" title="Racontera app home">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/branding/racontera-logo.png`}
                  alt="Racontera"
                  className="nav-logo-img"
                />
              </Link>
            </h1>
          </div>
          <div className="nav-end">
            <div className="nav-links">
              <div
                className="nav-dropdown"
                onMouseEnter={() => setResearchOpen(true)}
                onMouseLeave={() => setResearchOpen(false)}
              >
                <span className="nav-link nav-dropdown-toggle">
                  Research ▼
                </span>
                {researchOpen && (
                  <div className="dropdown-menu">
                    <Link to="/research/reports" className="dropdown-item">
                      Research reports
                    </Link>
                  </div>
                )}
              </div>

              <div
                className="nav-dropdown"
                onMouseEnter={() => setWriteOpen(true)}
                onMouseLeave={() => setWriteOpen(false)}
              >
                <span className="nav-link nav-dropdown-toggle">
                  Write ▼
                </span>
                {writeOpen && (
                  <div className="dropdown-menu">
                    <Link to="/content-queue" className="dropdown-item">
                      Draft candidates
                    </Link>
                    <Link to="/research-briefs" className="dropdown-item">
                      Draft workspaces
                    </Link>
                  </div>
                )}
              </div>

              <div
                className="nav-dropdown"
                onMouseEnter={() => setPublishOpen(true)}
                onMouseLeave={() => setPublishOpen(false)}
              >
                <span className="nav-link nav-dropdown-toggle">
                  Publish ▼
                </span>
                {publishOpen && (
                  <div className="dropdown-menu">
                    <Link to="/publishing-queue" className="dropdown-item">
                      Publishing queue
                    </Link>
                    <Link to="/published" className="dropdown-item">
                      Published
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <Link
              to="/account"
              className="nav-avatar-btn"
              title="Account and settings"
              aria-label="Account and settings"
            >
              <svg
                className="nav-avatar-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </Link>
          </div>
        </div>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <MarketingLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/blog', element: <BlogIndex /> },
      { path: '/blog/:slug', element: <BlogPost /> },
      { path: '/docs', element: <DocsIndex /> },
      { path: '/docs/:slug', element: <DocsPage /> },
    ],
  },
  { path: '/login', element: <Login /> },
  {
    element: <ProtectedLayout />,
    children: [
      {
        path: '/research/reports/archive',
        element: <ResearchReportsListPage archiveMode />,
      },
      { path: '/research/reports/:id', element: <ResearchReportDetailPage /> },
      { path: '/research/reports', element: <ResearchReportsListPage /> },
      {
        path: '/profiles',
        element: <Navigate to="/research/profiles" replace />,
      },
      {
        path: '/research/profiles',
        element: <Navigate to="/research/reports?manage=saved" replace />,
      },
      { path: '/content-queue', element: <ContentQueue /> },
      { path: '/research-briefs', element: <ResearchBriefs /> },
      { path: '/publishing-queue', element: <PublishingQueue /> },
      { path: '/published', element: <PublishedPlaceholder /> },
      { path: '/account', element: <AccountPage /> },
      { path: '/help', element: <HelpDocsIndex /> },
      { path: '/help/:slug', element: <HelpDocPage /> },
      {
        path: '/in-progress',
        element: <Navigate to="/research-briefs" replace />,
      },
      {
        path: '/content-analysis',
        element: <Navigate to="/research-briefs" replace />,
      },
      {
        path: '/seo-queue',
        element: <Navigate to="/research/reports" replace />,
      },
      {
        path: '/seo-drafts',
        element: <Navigate to="/research/reports" replace />,
      },
      {
        path: '/voice-profiles',
        element: <Navigate to="/research/profiles" replace />,
      },
      { path: '/research-brief/:draftId', element: <ResearchBriefWorkspace /> },
      { path: '/publish-preflight/:draftId', element: <PublishPreflight /> },
    ],
  },
]);

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
